/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
} from "@mui/material";
import {
    CreditCard,
    EventRepeat,
    ReplayCircleFilled,
    Autorenew,
} from "@mui/icons-material";

import { AppDispatch } from "../../../redux/store/store";
import {
    selectActiveBusiness,
    selectActiveBusinessId,
    selectActiveBusinessRole,
} from "../../../redux/selectors/collectSelector";
import {
    selectCollectSubscription,
    selectCollectSubscriptionLoading,
    selectCollectBillingSubmitting,
} from "../../../redux/selectors/collectBillingSelector";
import {
    fetchCollectSubscription,
    cancelCollectSubscription,
    reactivateCollectSubscription,
    updateCollectPaymentMethod,
} from "../../../redux/actions/collectBillingAction";
import { colors } from "../../../utility/types/constant";
import { useToast } from "../../../providers/ToastProvider";
import {
    CollectSubscriptionStatus,
    ICollectSubscriptionResult,
} from "../../../utility/types/collectTypes";
import {
    CollectPageWrapper,
    CollectHeroBanner,
    CollectHeroInner,
    CollectBadge,
    CollectHeroTitle,
    CollectHeroSubtitle,
    CollectContentArea,
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
    CollectSecondaryButton,
    CollectGhostButton,
    CollectInfoBanner,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
} from "../styles";
import CollectPaymentForm from "./CollectPaymentForm";
import {
    COLLECT_INTRO_CYCLES,
    collectIntroPrice,
    collectStandardPrice,
    formatCollectDate,
    daysUntil,
} from "./collectPlanPricing";

// ─────────────────────────────────────────────────────────────────────────
// CollectBilling — subscription management (Story 2).
//
// Owner view: plan phase, next charge, cancel / reactivate / update-card.
// Everyone else: read-only summary with a note to contact the owner.
// ─────────────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<CollectSubscriptionStatus, string> = {
    trialing: "Trial",
    trial_expired: "Trial expired",
    active: "Active",
    pending: "Pending",
    past_due: "Past due",
    pending_cancellation: "Cancels soon",
    cancelled: "Cancelled",
    inactive: "Inactive",
};

const STATUS_TONE: Record<
    CollectSubscriptionStatus,
    { bg: string; color: string }
> = {
    trialing: { bg: "rgba(59,130,246,0.12)", color: "#1e40af" },
    trial_expired: { bg: "rgba(107,114,128,0.15)", color: "#374151" },
    active: { bg: "rgba(30,193,149,0.14)", color: "#0f7a5b" },
    pending: { bg: "rgba(245,158,11,0.15)", color: "#b45309" },
    past_due: { bg: "rgba(220,38,38,0.12)", color: "#b91c1c" },
    pending_cancellation: { bg: "rgba(245,158,11,0.15)", color: "#b45309" },
    cancelled: { bg: "rgba(107,114,128,0.15)", color: "#374151" },
    inactive: { bg: "rgba(107,114,128,0.15)", color: "#374151" },
};

const StatusPill: React.FC<{ status: CollectSubscriptionStatus }> = ({
    status,
}) => {
    const tone = STATUS_TONE[status] || STATUS_TONE.inactive;
    return (
        <Box
            component="span"
            sx={{
                display: "inline-block",
                backgroundColor: tone.bg,
                color: tone.color,
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: "0.72rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                padding: "4px 12px",
                borderRadius: "999px",
            }}
        >
            {STATUS_LABEL[status] || status}
        </Box>
    );
};

// Human-readable card brand (Stripe returns lowercase codes like "visa").
const CARD_BRAND_LABEL: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
    diners: "Diners Club",
    jcb: "JCB",
    unionpay: "UnionPay",
};

const formatCardBrand = (brand?: string): string => {
    if (!brand) return "Card";
    return CARD_BRAND_LABEL[brand.toLowerCase()] || "Card";
};

/** "Visa •••• 4242" — display-safe label for the default card, or null. */
const formatCardLabel = (brand?: string, last4?: string): string | null =>
    last4 ? `${formatCardBrand(brand)} •••• ${last4}` : null;

/** "02/30" from month/year, or null when either is missing. */
const formatCardExpiry = (month?: number, year?: number): string | null => {
    if (!month || !year) return null;
    const mm = String(month).padStart(2, "0");
    const yy = String(year).slice(-2);
    return `${mm}/${yy}`;
};

const SummaryRow: React.FC<{ label: string; value: React.ReactNode }> = ({
    label,
    value,
}) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.2,
            borderBottom: "1px solid rgba(15,23,42,0.06)",
            gap: 2,
        }}
    >
        <Typography
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "0.85rem",
                color: "#6b7280",
            }}
        >
            {label}
        </Typography>
        <Box
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "0.9rem",
                fontWeight: 600,
                color: "#1f2937",
                textAlign: "right",
            }}
        >
            {value}
        </Box>
    </Box>
);

const CollectBilling: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();

    const activeBusiness = useSelector(selectActiveBusiness);
    const activeBusinessId = useSelector(selectActiveBusinessId);
    const role = useSelector(selectActiveBusinessRole);
    const subscription = useSelector(selectCollectSubscription);
    const isLoading = useSelector(selectCollectSubscriptionLoading);
    const isSubmitting = useSelector(selectCollectBillingSubmitting);

    const isOwner = role === "owner";

    const [showCardForm, setShowCardForm] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");

    const currency = useMemo(
        () => subscription?.currency || activeBusiness?.currency || "usd",
        [subscription?.currency, activeBusiness?.currency],
    );

    useEffect(() => {
        if (activeBusinessId) {
            dispatch(fetchCollectSubscription(undefined));
        }
    }, [dispatch, activeBusinessId]);

    const isIntro = subscription?.planPhase === "intro";
    const currentPrice = isIntro
        ? collectIntroPrice(currency)
        : collectStandardPrice(currency);

    // Display-safe card snapshot (brand + last4 + expiry) for the "on file" card.
    const cardLabel = useMemo(
        () => formatCardLabel(subscription?.cardBrand, subscription?.cardLast4),
        [subscription?.cardBrand, subscription?.cardLast4],
    );
    const cardExpiry = useMemo(
        () =>
            formatCardExpiry(
                subscription?.cardExpMonth,
                subscription?.cardExpYear,
            ),
        [subscription?.cardExpMonth, subscription?.cardExpYear],
    );

    // ── Actions ───────────────────────────────────────────────────────────
    const handleUpdatePaymentMethod = async (
        paymentMethodId: string,
    ): Promise<ICollectSubscriptionResult> => {
        const updated = await dispatch(
            updateCollectPaymentMethod({ paymentMethodId }),
        ).unwrap();
        // Payment-method updates never trigger an SCA challenge here.
        return { subscription: updated, requiresAction: false };
    };

    const handlePaymentMethodSuccess = () => {
        setShowCardForm(false);
        toast.success("Your payment method has been updated.");
    };

    const handleConfirmCancel = async () => {
        try {
            await dispatch(
                cancelCollectSubscription({
                    reason: cancelReason.trim() || undefined,
                    immediate: false,
                }),
            ).unwrap();
            setCancelOpen(false);
            setCancelReason("");
            toast.success(
                "Your subscription is set to cancel. You won't be charged again.",
            );
        } catch (e: any) {
            toast.error(e?.message || "We couldn't cancel. Please try again.");
        }
    };

    const handleReactivate = async () => {
        try {
            await dispatch(reactivateCollectSubscription(undefined)).unwrap();
            toast.success("Your subscription has been reactivated.");
        } catch (e: any) {
            toast.error(
                e?.message || "We couldn't reactivate. Please try again.",
            );
        }
    };

    // ── Render states ─────────────────────────────────────────────────────
    if (isLoading && !subscription) {
        return (
            <CollectPageWrapper>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "50vh",
                    }}
                >
                    <CircularProgress sx={{ color: colors.mainSecondary }} />
                </Box>
            </CollectPageWrapper>
        );
    }

    const noSubscription =
        !subscription ||
        subscription.status === "inactive" ||
        subscription.status === "trial_expired" ||
        subscription.status === "cancelled";

    // Trial with no card on file yet — the business hasn't actually subscribed.
    // Show a "Subscribe now" path instead of the manage (update-card / cancel)
    // panel, which has no Stripe subscription to act on.
    const isTrialOnly = !noSubscription && subscription?.status === "trialing";
    const trialDaysLeft =
        isTrialOnly && subscription?.trialEndsAt
            ? Math.max(0, daysUntil(subscription.trialEndsAt))
            : null;

    return (
        <CollectPageWrapper>
            <CollectHeroBanner>
                <CollectHeroInner>
                    <CollectBadge label="Billing" />
                    <CollectHeroTitle>Subscription & billing</CollectHeroTitle>
                    <CollectHeroSubtitle>
                        Manage your Collect plan, payment method, and renewals.
                    </CollectHeroSubtitle>
                </CollectHeroInner>
            </CollectHeroBanner>

            <CollectContentArea>
                {!isOwner && (
                    <CollectInfoBanner tone="info">
                        You have read-only access to billing. Contact your
                        business owner to make changes.
                    </CollectInfoBanner>
                )}

                {noSubscription ? (
                    <CollectSectionCard>
                        <CollectEmptyState>
                            <CollectEmptyTitle>
                                No active subscription
                            </CollectEmptyTitle>
                            <CollectEmptyHint>
                                Subscribe to Collect to automate reminders,
                                track promises, and recover overdue invoices.
                            </CollectEmptyHint>
                            {isOwner && (
                                <Box sx={{ mt: 3 }}>
                                    <CollectPrimaryButton
                                        onClick={() =>
                                            navigate("/collect/subscribe")
                                        }
                                    >
                                        View plans
                                    </CollectPrimaryButton>
                                </Box>
                            )}
                        </CollectEmptyState>
                    </CollectSectionCard>
                ) : isTrialOnly ? (
                    <Grid container spacing={3}>
                        {/* ── Trial overview ───────────────────────────── */}
                        <Grid item xs={12} md={7}>
                            <CollectSectionCard>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                    }}
                                >
                                    <CollectSectionTitle>
                                        Your plan
                                    </CollectSectionTitle>
                                    <StatusPill status="trialing" />
                                </Box>

                                <CollectInfoBanner tone="info">
                                    {trialDaysLeft != null
                                        ? `You're on a free trial, ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"
                                        } left. Subscribe now to keep full access to Collect.`
                                        : "You're on a free trial. Subscribe now to keep full access to Collect."}
                                </CollectInfoBanner>

                                <Box sx={{ mt: 1 }}>
                                    <SummaryRow
                                        label="Plan"
                                        value="Collect Monthly"
                                    />
                                    <SummaryRow
                                        label="Intro rate"
                                        value={`${collectIntroPrice(
                                            currency,
                                        )}/month for ${COLLECT_INTRO_CYCLES} months`}
                                    />
                                    <SummaryRow
                                        label="Standard rate"
                                        value={`${collectStandardPrice(
                                            currency,
                                        )}/month after intro`}
                                    />
                                    {subscription?.trialEndsAt && (
                                        <SummaryRow
                                            label="Trial ends"
                                            value={formatCollectDate(
                                                subscription.trialEndsAt,
                                            )}
                                        />
                                    )}
                                </Box>
                            </CollectSectionCard>
                        </Grid>

                        {/* ── Subscribe action ─────────────────────────── */}
                        <Grid item xs={12} md={5}>
                            <CollectSectionCard>
                                <CollectSectionTitle>
                                    Subscribe
                                </CollectSectionTitle>
                                <CollectSectionSubtitle>
                                    {isOwner
                                        ? "Add your payment details to continue with Collect after your trial."
                                        : "Only the owner can add a payment method and subscribe."}
                                </CollectSectionSubtitle>

                                {isOwner && (
                                    <Box sx={{ mt: 2 }}>
                                        <CollectPrimaryButton
                                            fullWidth
                                            onClick={() =>
                                                navigate("/collect/subscribe")
                                            }
                                            startIcon={
                                                <CreditCard
                                                    sx={{ fontSize: 18 }}
                                                />
                                            }
                                        >
                                            Subscribe now
                                        </CollectPrimaryButton>
                                    </Box>
                                )}
                            </CollectSectionCard>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={3}>
                        {/* ── Overview ─────────────────────────────────── */}
                        <Grid item xs={12} md={7}>
                            <CollectSectionCard>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                    }}
                                >
                                    <CollectSectionTitle>
                                        Your plan
                                    </CollectSectionTitle>
                                    {subscription && (
                                        <StatusPill
                                            status={subscription.status}
                                        />
                                    )}
                                </Box>

                                {subscription?.status ===
                                    "pending_cancellation" && (
                                        <CollectInfoBanner tone="warn">
                                            Your subscription will end on{" "}
                                            {formatCollectDate(
                                                subscription.currentPeriodEnd,
                                            )}
                                            . You won't be charged again. Your account
                                            stays ready for 60 days, come back anytime
                                            and resubscribe in one click.
                                        </CollectInfoBanner>
                                    )}

                                {subscription?.status === "past_due" && (
                                    <CollectInfoBanner tone="warn">
                                        Your last payment didn't go through. Please
                                        update your payment method to keep Collect
                                        active.
                                    </CollectInfoBanner>
                                )}

                                <Box sx={{ mt: 1 }}>
                                    <SummaryRow
                                        label="Plan"
                                        value="Collect Monthly"
                                    />
                                    <SummaryRow
                                        label="Current rate"
                                        value={`${currentPrice}/month`}
                                    />
                                    {isIntro &&
                                        subscription?.introChargesRemaining !=
                                        null && (
                                            <SummaryRow
                                                label="Intro months remaining"
                                                value={
                                                    subscription.introChargesRemaining
                                                }
                                            />
                                        )}
                                    {isIntro && subscription?.introEndsAt && (
                                        <SummaryRow
                                            label="Standard rate starts"
                                            value={`${collectStandardPrice(
                                                currency,
                                            )}/month on ${formatCollectDate(
                                                subscription.introEndsAt,
                                            )}`}
                                        />
                                    )}
                                    <SummaryRow
                                        label={
                                            subscription?.cancelAtPeriodEnd
                                                ? "Access until"
                                                : "Next charge"
                                        }
                                        value={
                                            subscription?.cancelAtPeriodEnd
                                                ? formatCollectDate(
                                                    subscription.currentPeriodEnd,
                                                )
                                                : `${currentPrice} on ${formatCollectDate(
                                                    subscription?.currentPeriodEnd,
                                                )}`
                                        }
                                    />
                                    {cardLabel && (
                                        <SummaryRow
                                            label="Payment method"
                                            value={
                                                cardExpiry
                                                    ? `${cardLabel} · exp ${cardExpiry}`
                                                    : cardLabel
                                            }
                                        />
                                    )}
                                </Box>
                            </CollectSectionCard>
                        </Grid>

                        {/* ── Actions ──────────────────────────────────── */}
                        <Grid item xs={12} md={5}>
                            <CollectSectionCard>
                                <CollectSectionTitle>
                                    Manage
                                </CollectSectionTitle>
                                <CollectSectionSubtitle>
                                    {isOwner
                                        ? "Update your card or change your plan."
                                        : "Only the owner can make changes."}
                                </CollectSectionSubtitle>

                                {isOwner && (
                                    <Box sx={{ mt: 2 }}>
                                        {/* Reactivate (pending cancellation) */}
                                        {subscription?.status ===
                                            "pending_cancellation" && (
                                                <CollectPrimaryButton
                                                    fullWidth
                                                    onClick={handleReactivate}
                                                    disabled={isSubmitting}
                                                    startIcon={
                                                        <ReplayCircleFilled
                                                            sx={{ fontSize: 18 }}
                                                        />
                                                    }
                                                    sx={{ mb: 1.5 }}
                                                >
                                                    Keep my subscription
                                                </CollectPrimaryButton>
                                            )}

                                        {/* Update payment method */}
                                        {!showCardForm ? (
                                            <>
                                                {cardLabel && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            mb: 1.5,
                                                            px: 1.5,
                                                            py: 1.2,
                                                            borderRadius: "10px",
                                                            backgroundColor:
                                                                "rgba(15,23,42,0.04)",
                                                        }}
                                                    >
                                                        <CreditCard
                                                            sx={{
                                                                fontSize: 20,
                                                                color: "#6b7280",
                                                            }}
                                                        />
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Montserrat', sans-serif",
                                                                    fontSize:
                                                                        "0.85rem",
                                                                    fontWeight: 600,
                                                                    color: "#1f2937",
                                                                }}
                                                            >
                                                                {cardLabel}
                                                            </Typography>
                                                            {cardExpiry && (
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily:
                                                                            "'Montserrat', sans-serif",
                                                                        fontSize:
                                                                            "0.72rem",
                                                                        color: "#6b7280",
                                                                    }}
                                                                >
                                                                    Expires{" "}
                                                                    {cardExpiry}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                                <CollectSecondaryButton
                                                    fullWidth
                                                    onClick={() =>
                                                        setShowCardForm(true)
                                                    }
                                                    startIcon={
                                                        <CreditCard
                                                            sx={{ fontSize: 18 }}
                                                        />
                                                    }
                                                    sx={{ mb: 1.5 }}
                                                >
                                                    {cardLabel
                                                        ? "Update payment method"
                                                        : "Add payment method"}
                                                </CollectSecondaryButton>
                                            </>
                                        ) : (
                                            <Box sx={{ mb: 2 }}>
                                                <CollectPaymentForm
                                                    submitLabel="Save new card"
                                                    onCreatePaymentMethod={
                                                        handleUpdatePaymentMethod
                                                    }
                                                    onSuccess={
                                                        handlePaymentMethodSuccess
                                                    }
                                                    defaultCardholderName={
                                                        activeBusiness?.name
                                                    }
                                                    busy={isSubmitting}
                                                />
                                                <CollectGhostButton
                                                    fullWidth
                                                    onClick={() =>
                                                        setShowCardForm(false)
                                                    }
                                                    sx={{ mt: 1 }}
                                                >
                                                    Cancel
                                                </CollectGhostButton>
                                            </Box>
                                        )}

                                        {/* Cancel subscription */}
                                        {subscription?.status !==
                                            "pending_cancellation" && (
                                                <CollectGhostButton
                                                    fullWidth
                                                    onClick={() =>
                                                        setCancelOpen(true)
                                                    }
                                                    disabled={isSubmitting}
                                                    startIcon={
                                                        <EventRepeat
                                                            sx={{ fontSize: 18 }}
                                                        />
                                                    }
                                                >
                                                    Cancel subscription
                                                </CollectGhostButton>
                                            )}
                                    </Box>
                                )}
                            </CollectSectionCard>
                        </Grid>
                    </Grid>
                )}
            </CollectContentArea>

            {/* ── Cancel confirmation dialog ───────────────────────────── */}
            <Dialog
                open={cancelOpen}
                onClose={() => !isSubmitting && setCancelOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        color: colors.primary,
                    }}
                >
                    Cancel your subscription?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.9rem",
                            color: "#374151",
                        }}
                    >
                        You'll keep access until{" "}
                        {formatCollectDate(subscription?.currentPeriodEnd)}, and
                        you won't be charged again. Your account stays ready for
                        60 days — come back anytime and resubscribe in one click.
                    </DialogContentText>
                    <TextField
                        label="Tell us why (optional)"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <CollectGhostButton
                        onClick={() => setCancelOpen(false)}
                        disabled={isSubmitting}
                    >
                        Keep subscription
                    </CollectGhostButton>
                    <CollectPrimaryButton
                        onClick={handleConfirmCancel}
                        disabled={isSubmitting}
                        startIcon={
                            isSubmitting ? (
                                <CircularProgress
                                    size={16}
                                    sx={{ color: "#fff" }}
                                />
                            ) : (
                                <Autorenew sx={{ fontSize: 18 }} />
                            )
                        }
                    >
                        Confirm cancellation
                    </CollectPrimaryButton>
                </DialogActions>
            </Dialog>
        </CollectPageWrapper>
    );
};

export default CollectBilling;
